import api from '../api/client.js';

const posSettingsService = {
  // ✅ ENHANCED: Get POS settings with comprehensive error handling
  getSettings: async () => {
    try {
      console.log('🔄 Fetching POS settings...');
      const response = await api.get('/api/v1/pos/settings');
      
      // ✅ CRITICAL: Validate response structure
      if (!response?.data) {
        console.warn('⚠️ No data in response, returning defaults');
        return posSettingsService.getDefaultSettings();
      }

      console.log('✅ POS settings fetched:', response.data);
      
      // ✅ ENHANCED: Validate critical arrays exist
      const data = response.data;
      
      // Ensure cartAddOns is an array
      if (data.cartAddOns && !Array.isArray(data.cartAddOns)) {
        console.warn('⚠️ cartAddOns is not an array, fixing...');
        data.cartAddOns = [];
      }
      
      // Ensure cartMetadata is an object
      if (data.cartMetadata && typeof data.cartMetadata !== 'object') {
        console.warn('⚠️ cartMetadata is not an object, fixing...');
        data.cartMetadata = {};
      }
      
      // Ensure paymentMethods is an object
      if (data.paymentMethods && typeof data.paymentMethods !== 'object') {
        console.warn('⚠️ paymentMethods is not an object, fixing...');
        data.paymentMethods = {};
      }
      
      return data;
    } catch (error) {
      console.error('❌ Error fetching POS settings:', error);
      
      // ✅ CRITICAL: Return defaults on any error
      console.log('🔄 Returning default settings due to error');
      return posSettingsService.getDefaultSettings();
    }
  },

  // ✅ ENHANCED: Default settings factory
  getDefaultSettings: () => {
    return {
      cartAddOns: [
        {
          id: 'service_charge',
          name: 'Service Charge',
          type: 'percentage',
          value: 10,
          enabled: true,
          visible: true,
          order: 1,
          editable: true
        },
        {
          id: 'gst',
          name: 'GST',
          type: 'percentage',
          value: 9,
          enabled: true,
          visible: true,
          order: 2,
          editable: true
        },
        {
          id: 'discount',
          name: 'Discount',
          type: 'discount',
          value: 0,
          enabled: false,
          visible: true,
          order: 3,
          editable: true
        }
      ],
      cartMetadata: {
        deviceNumber: {
          enabled: true,
          visible: true,
          required: false,
          label: 'Device Number',
          placeholder: 'Enter device number...'
        },
        tableNumber: {
          enabled: false,
          visible: false,
          required: false,
          label: 'Table Number',
          placeholder: 'Enter table number...'
        },
        customerName: {
          enabled: false,
          visible: false,
          required: false,
          label: 'Customer Name',
          placeholder: 'Enter customer name...'
        },
        notes: {
          enabled: false,
          visible: false,
          required: false,
          label: 'Order Notes',
          placeholder: 'Special instructions...'
        }
      },
      paymentMethods: {
        exactCash: {
          id: 'exact_cash',
          name: 'Exact Cash',
          enabled: true,
          visible: true,
          icon: 'MonetizationOn',
          order: 1
        },
        calculateChange: {
          id: 'calculate_change',
          name: 'Calculate Change',
          enabled: true,
          visible: true,
          icon: 'Calculate',
          order: 2
        },
        qrCode: {
          id: 'qr_code',
          name: 'QR Payment',
          enabled: false,
          visible: true,
          icon: 'QrCode',
          order: 3,
          comingSoon: true
        },
        creditCard: {
          id: 'credit_card',
          name: 'Credit Card',
          enabled: false,
          visible: true,
          icon: 'CreditCard',
          order: 4,
          comingSoon: true
        }
      },
      defaultSettings: {
        currency: 'USD',
        currencySymbol: '$',
        decimalPlaces: 2,
        autoCalculateTax: true,
        showItemTotals: true,
        allowNegativeQuantity: false,
        roundingMethod: 'round',
        defaultPaymentMethod: 'exact_cash'
      },
      _metadata: {
        stored_in_database: false,
        fallback_defaults: true
      }
    };
  },

  // Save POS settings
  saveSettings: async (settings) => {
    try {
      console.log('💾 Saving POS settings:', settings);
      
      // ✅ Validate before sending
      if (!settings || typeof settings !== 'object') {
        throw new Error('Invalid settings format');
      }
      
      const response = await api.post('/api/v1/pos/settings', settings);
      console.log('✅ POS settings saved:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error saving POS settings:', error);
      throw error;
    }
  },

  // Update specific settings
  updateSettings: async (settings) => {
    console.log('🔄 Updating POS settings:', settings);
    
    try {
      // ✅ Validate before sending
      if (!settings || typeof settings !== 'object') {
        throw new Error('Invalid settings format');
      }
      
      const response = await api.put('/api/v1/pos/settings', settings);
      console.log('✅ POS settings updated:', response.data);
      
      // ✅ Emit custom event for other components to listen
      window.dispatchEvent(new CustomEvent('pos-settings-updated', {
        detail: response.data.settings
      }));
      
      return response.data;
    } catch (error) {
      console.error('❌ Error updating POS settings:', error);
      throw error;
    }
  },

  // Reset settings to defaults
  resetSettings: async () => {
    try {
      console.log('🔄 Resetting POS settings to defaults...');
      const response = await api.delete('/api/v1/pos/settings');
      console.log('✅ POS settings reset:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error resetting POS settings:', error);
      throw error;
    }
  }
};

export default posSettingsService;